import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';

// ── API ───────────────────────────────────────────────────────────────────────
const API = 'https://api.naijapredict.net';

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('creator_token');
  const res = await fetch(API + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...opts.headers,
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ── Auth Context ──────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('creator_token');
    if (token) {
      apiFetch('/api/creator/me')
        .then(setCreator)
        .catch(() => localStorage.removeItem('creator_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  function logout() {
    localStorage.removeItem('creator_token');
    setCreator(null);
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#07090a', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#00c566', fontSize:14 }}>Loading...</div>
    </div>
  );

  return <AuthCtx.Provider value={{ creator, setCreator, logout }}>{children}</AuthCtx.Provider>;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const NAV = [
  { to:'/dashboard',  Icon:LayoutDashboard, label:'Dashboard'     },
  { to:'/markets',    Icon:BarChart2,       label:'My Markets'    },
  { to:'/create',     Icon:PlusCircle,      label:'Create Market' },
  { to:'/analytics',  Icon:TrendingUp,      label:'Analytics'     },
  { to:'/payouts',    Icon:Wallet,          label:'Payouts'       },
];

function Layout({ children }) {
  const { creator, logout } = useAuth();
  const [mob, setMob] = useState(false);

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#07090a', fontFamily:"'Inter',sans-serif", color:'#e8eaeb' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Syne:wght@700;800;900&display=swap');
        * { box-sizing: border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:4px; } ::-webkit-scrollbar-track { background:#0d1114; } ::-webkit-scrollbar-thumb { background:#2a3540; border-radius:2px; }
        a { text-decoration:none; color:inherit; }
        .nav-link { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; font-size:14px; font-weight:500; color:#4a5a63; transition:all .15s; margin-bottom:4px; }
        .nav-link:hover { background:rgba(255,255,255,.04); color:#e8eaeb; }
        .nav-link.active { background:rgba(0,197,102,.08); color:#00c566; border-left:3px solid #00c566; }
        .btn { padding:10px 20px; border-radius:10px; border:none; cursor:pointer; font-weight:600; font-size:14px; transition:all .15s; }
        .btn-primary { background:linear-gradient(135deg,#00c566,#00a854); color:#000; }
        .btn-secondary { background:rgba(255,255,255,.06); color:#e8eaeb; border:1px solid rgba(255,255,255,.08); }
        .btn-danger { background:rgba(255,68,68,.1); color:#ff4444; border:1px solid rgba(255,68,68,.2); }
        .card { background:#0d1114; border:1px solid rgba(255,255,255,.06); border-radius:16px; padding:20px; }
        input,textarea,select { background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); border-radius:10px; padding:12px 14px; color:#e8eaeb; font-size:14px; width:100%; outline:none; font-family:inherit; transition:border .15s; }
        input:focus,textarea:focus,select:focus { border-color:#00c566; }
        label { font-size:12px; color:#4a5a63; font-weight:600; letter-spacing:.5px; display:block; margin-bottom:6px; }
        @media(max-width:768px) { .sidebar { display:none; } .sidebar.open { display:flex; position:fixed; inset:0; z-index:200; } }
      `}</style>

      {/* Sidebar */}
      <aside className={`sidebar${mob?' open':''}`} style={{ width:240, background:'#0d1114', borderRight:'1px solid rgba(255,255,255,.06)', display:'flex', flexDirection:'column', position:'fixed', top:0, left:0, height:'100vh', zIndex:100 }}>
        {/* Logo */}
        <div style={{ padding:'16px', borderBottom:'1px solid rgba(255,255,255,.06)', display:'flex', alignItems:'center', gap:10 }}>
          <img src="/logo.svg" alt="NaijaPredict" style={{ width:36, height:36, borderRadius:8, flexShrink:0 }}/>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:900, fontSize:15, color:'#fff', lineHeight:1 }}>NAIJAPREDICT</div>
            <div style={{ fontSize:10, color:'#00c566', fontWeight:600, letterSpacing:1, marginTop:2 }}>CREATOR PORTAL</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'12px 8px', overflowY:'auto' }}>
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} className={({isActive})=>'nav-link'+(isActive?' active':'')} onClick={()=>setMob(false)}>
              <n.Icon size={17} strokeWidth={1.8}/>{n.label}
            </NavLink>
          ))}
        </nav>

        {/* Creator Info */}
        <div style={{ padding:'14px 16px', borderTop:'1px solid rgba(255,255,255,.06)' }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{creator?.name || 'Creator'}</div>
          <div style={{ fontSize:11, color:'#4a5a63', marginBottom:10 }}>@{creator?.username || 'unknown'}</div>
          <button className="btn btn-secondary" onClick={logout} style={{ width:'100%', padding:'8px', fontSize:12 }}>Sign out</button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mob && <div onClick={()=>setMob(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:99 }}/>}

      {/* Main */}
      <main style={{ flex:1, marginLeft:240, minHeight:'100vh', display:'flex', flexDirection:'column' }}>
        {/* Mobile header */}
        <header style={{ display:'none', padding:'0 16px', height:56, borderBottom:'1px solid rgba(255,255,255,.06)', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#0d1114', zIndex:50 }} className="mobile-header">
          <button onClick={()=>setMob(true)} style={{ background:'none', border:'none', color:'#e8eaeb', fontSize:22, cursor:'pointer' }}>☰</button>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:900, fontSize:16 }}>CREATOR PORTAL</div>
          <div/>
        </header>
        <div style={{ flex:1, padding:'24px', maxWidth:1100, width:'100%', margin:'0 auto' }}>
          {children}
        </div>
      </main>
    </div>
  );
}

// ── Protected Route ───────────────────────────────────────────────────────────
function Protected({ children }) {
  const { creator } = useAuth();
  return creator ? children : <Navigate to="/login" replace/>;
}

// ── Pages (placeholders) ──────────────────────────────────────────────────────
import { LayoutDashboard, BarChart2, PlusCircle, TrendingUp, Wallet } from 'lucide-react';
import LoginPage    from './pages/LoginPage';
import Dashboard    from './pages/Dashboard';
import Markets      from './pages/Markets';
import CreateMarket from './pages/CreateMarket';
import Analytics    from './pages/Analytics';
import Payouts      from './pages/Payouts';

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage/>}/>
          <Route path="/*" element={
            <Protected>
              <Layout>
                <Routes>
                  <Route path="/dashboard"  element={<Dashboard/>}/>
                  <Route path="/markets"    element={<Markets/>}/>
                  <Route path="/create"     element={<CreateMarket/>}/>
                  <Route path="/analytics"  element={<Analytics/>}/>
                  <Route path="/payouts"    element={<Payouts/>}/>
                  <Route path="*"           element={<Navigate to="/dashboard" replace/>}/>
                </Routes>
              </Layout>
            </Protected>
          }/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
